import { useLayoutEffect, useMemo, useState } from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';

import { WorkspaceProvider } from './context/WorkspaceContext';
import { UserProvider } from './context/UserContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { isAddedVectorLayer } from './map/utils';
import AppRoutes from './AppRoutes';
import CatalogContext from './context/CatalogContext';
import './global.css';
import { DownloadProvider } from './context/DownloadContext';

const Wrapper = ({ children }) => {
  const location = useLocation();
  useLayoutEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant',
    });
  }, [location.pathname]);
  return children;
};
function App() {
  const queryClient = new QueryClient();
  // Workspace Context
  // const [selectedKeys, setSelectedKeys] = useState([]);
  // const [defaultKeys, setDefaultKeys] = useState([]);
  const [valueRanges, setValueRanges] = useState({});
  const [checkedDatasets, setCheckedDatasets] = useState({});
  const [filtersDatasets, setFilterDatasets] = useState({});
  const [filterItems, setFilterItems] = useState({});
  const [opacities, setOpacities] = useState({});
  // Per-layer legend recolors for ArcGIS feature services: {itemKey: {entryKey: color}}
  const [layerColors, setLayerColors] = useState({});
  // Unique Layers
  const [layersByUser, setLayersByUser] = useState([]);
  const [clipLayer, setClipLayer] = useState([]);
  // Visualization filter
  const [selectedClipBoundary, setSelectedClipBoundary] = useState(null);
  const [selectedClipSubset, setSelectedClipSubset] = useState(null);
  const [selectedCustomClipKey, setSelectedCustomClipKey] = useState(null);
  const [clipEnabled, setClipEnabled] = useState(false);
  const [highlightClip, setHighlightClip] = useState(true);
  const [addDataType, setAddDataType] = useState('Generic');

  // Catalog Context
  const [ckanData, setCkanData] = useState(null);
  const [collectionNames, setCollectionNames] = useState('');
  const [pillar, setPillar] = useState('');
  const [element, setElement] = useState('');
  const [collectionsList, setCollectionList] = useState(null);
  const [pillarList, setPillarList] = useState(null);
  const [elementList, setElementList] = useState(null);

  const [currentDatasets, setCurrentDatasets] = useState([]); // per page
  const [numDatasets, setNumDatasets] = useState(0); // total datasets to display in pages
  const [searchResults, setSearchResults] = useState([]); // total datasets upon searching
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFlag, setSearchFlag] = useState(false);
  const [searchType] = useState('substring_search');
  const [spatialQuery, setSpatialQuery] = useState('');
  const contextValue = useMemo(
    () => ({
      ckanData,
      setCkanData,
      collectionNames,
      setCollectionNames,
      pillar,
      setPillar,
      element,
      setElement,
      collectionsList,
      setCollectionList,
      pillarList,
      setPillarList,
      elementList,
      setElementList,
      currentDatasets,
      setCurrentDatasets,
      numDatasets,
      setNumDatasets,
      searchResults,
      setSearchResults,
      searchQuery,
      setSearchQuery,
      searchFlag,
      setSearchFlag,
      searchType,
      spatialQuery,
      setSpatialQuery,
    }),
    [
      ckanData,
      collectionNames,
      pillar,
      element,
      collectionsList,
      pillarList,
      elementList,
      currentDatasets,
      numDatasets,
      searchResults,
      searchQuery,
      searchFlag,
      searchType,
      spatialQuery,
    ]
  );
  // "creation_method":

  const getGisService = (dataset) => {
    if (isAddedVectorLayer(dataset)) return null;
    if (dataset.gis_services) {
      return dataset.gis_services.filter((e) => e.service_type === 'ArcGIS')
        .length > 0
        ? dataset.gis_services.filter((e) => e.service_type === 'ArcGIS')[0]
        : dataset.gis_services[0];
    }
    let format = 'raster';
    let dataset_collections = {};
    let categorical_values = null;
    dataset.extras.forEach((object) => {
      if (object.key === 'collection_name') {
        const obj = {};
        obj['name'] = object.value;
        Object.assign(dataset_collections, obj);
        if (
          object.value === 'California Interagency Treatment Tracking System' ||
          object.value === 'Boundary Datasets'
        ) {
          format = 'vector';
        }
      } else if (object.key === 'format') {
        if (
          object.value === 'ArcGIS Feature Service' ||
          object.value === 'Shapefile'
        ) {
          format = 'vector';
        }
        const obj = {};
        obj[object.key] = object.value;
        Object.assign(dataset_collections, obj);
      } else if (object.key === 'categorical_values') {
        categorical_values = JSON.parse(object.value);
      } else {
        const obj = {};
        obj[object.key] = object.value;
        Object.assign(dataset_collections, obj);
      }
    });
    for (let k = 0; k < dataset.resources.length; k++) {
      if (
        dataset.resources[k].name.search('ITS') !== -1 &&
        dataset.resources[k].format === 'WFS'
      ) {
        if (
          dataset.resources[k].name.search('activities') !== -1 ||
          dataset.resources[k].name.search('activity') !== -1
        ) {
          const item = dataset.resources[k];
          const obj = {
            service_url: item.url,
            layer_name: item.wfs_layer,
            data_type: format,
            dataset_collections: [dataset_collections],
            color_map: null,
            service_type: item.service_type ?? null,
            categorical_values: categorical_values,
          };
          return obj;
        }
      } else {
        let item = null;
        if (dataset.resources[k].format === 'WMS' && format === 'raster') {
          item = dataset.resources[k];
          let colorEntries = null;

          if (item.legend) {
            const colorData = JSON.parse(item.legend);
            if (colorData.length > 0) {
              if (colorData[0].value < colorData[1].value) {
                colorEntries = {
                  color_map_entries: [
                    {
                      color: colorData[0].color,

                      float_value: colorData[0].value,
                    },
                    {
                      color: colorData[1].color,

                      float_value: colorData[1].value,
                    },
                  ],
                };
              } else {
                colorEntries = {
                  color_map_entries: [
                    {
                      color: colorData[1].color,

                      float_value: colorData[1].value,
                    },
                    {
                      color: colorData[0].color,

                      float_value: colorData[0].value,
                    },
                  ],
                };
              }
            }
          }
          const obj = {
            service_url: item.url,
            layer_name: item.wms_layer,
            data_type: format,
            dataset_collections: [dataset_collections],
            color_map: colorEntries,
            service_type: item.service_type ?? null,
            categorical_values: categorical_values,
          };
          return obj;
        } else if (
          dataset.resources[k].format === 'WMS' &&
          format === 'vector'
        ) {
          item = dataset.resources[k];

          const obj = {
            service_url: item.url,
            layer_name: item.wms_layer,
            data_type: format,
            dataset_collections: [dataset_collections],
            color_map: null,
            service_type: item.service_type ?? null,
            categorical_values: categorical_values,
          };
          return obj;
        } else if (dataset.resources[k].format === 'ArcGIS Feature Service') {
          item = dataset.resources[k];
          let arcgisColorMap = null;
          if (item.legend) {
            try {
              const legend = JSON.parse(item.legend);
              arcgisColorMap = { type: 'categorical', ...legend };
            } catch {
              arcgisColorMap = null;
            }
          }

          const obj = {
            service_url: item.url,
            layer_name: item.name,
            data_type: 'vector',
            dataset_collections: [dataset_collections],
            color_map: arcgisColorMap,
            service_type: 'ArcGIS',
            categorical_values: categorical_values,
          };
          return obj;
        } else if (dataset.resources[k].format === 'ArcGIS Tile Layer') {
          // Pre-rendered raster tiles from a cached ArcGIS MapServer: no
          // queryable features, so its own data_type/service_type keep it
          // out of both the raster (WMS/WCS) and vector code paths.
          item = dataset.resources[k];
          // Bounding box of the ingested extent, so the layer doesn't request
          // (and 404 on) empty tiles outside the cache's coverage.
          let extent = null;
          const spatial = dataset.extras?.find((e) => e.key === 'spatial');
          if (spatial) {
            try {
              const coords = JSON.parse(spatial.value).coordinates.flat(Infinity);
              const xs = coords.filter((_, i) => i % 2 === 0);
              const ys = coords.filter((_, i) => i % 2 === 1);
              extent = [
                Math.min(...xs),
                Math.min(...ys),
                Math.max(...xs),
                Math.max(...ys),
              ];
            } catch {
              extent = null;
            }
          }
          return {
            service_url: item.url.replace(/\/tile\/\{z\}\/\{y\}\/\{x\}$/, ''),
            extent_4326: extent,
            tile_url: item.url,
            min_zoom: item.min_zoom,
            max_zoom: item.max_zoom,
            layer_name: item.name,
            data_type: 'tile',
            dataset_collections: [dataset_collections],
            color_map: null,
            service_type: 'ArcGISTile',
            categorical_values: categorical_values,
          };
        }
      }
    }

    // return dataset.gis_services[0]
  };

  const getItemKey = (dataset) => {
    if (isAddedVectorLayer(dataset)) return dataset.get('name');
    return (
      getGisService(dataset).service_url +
      '/' +
      getGisService(dataset).layer_name
    );
  };

  // if(Object.keys(user).length === 0 || user?.username == undefined){
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Wrapper>
          <UserProvider>
            <WorkspaceProvider>
              <CatalogContext.Provider value={contextValue}>
                <DownloadProvider>
                  <AppRoutes
                    getGisService={getGisService}
                    getItemKey={getItemKey}
                    filtersDatasets={filtersDatasets}
                    setFilterDatasets={setFilterDatasets}
                    checkedDatasets={checkedDatasets}
                    setCheckedDatasets={setCheckedDatasets}
                    filterItems={filterItems}
                    setFilterItems={setFilterItems}
                    opacities={opacities}
                    setOpacities={setOpacities}
                    layerColors={layerColors}
                    setLayerColors={setLayerColors}
                    valueRanges={valueRanges}
                    setValueRanges={setValueRanges}
                    addDataType={addDataType}
                    setAddDataType={setAddDataType}
                    layersByUser={layersByUser}
                    setLayersByUser={setLayersByUser}
                    clipLayer={clipLayer}
                    setClipLayer={setClipLayer}
                    selectedClipBoundary={selectedClipBoundary}
                    setSelectedClipBoundary={setSelectedClipBoundary}
                    selectedClipSubset={selectedClipSubset}
                    setSelectedClipSubset={setSelectedClipSubset}
                    selectedCustomClipKey={selectedCustomClipKey}
                    setSelectedCustomClipKey={setSelectedCustomClipKey}
                    clipEnabled={clipEnabled}
                    setClipEnabled={setClipEnabled}
                    highlightClip={highlightClip}
                    setHighlightClip={setHighlightClip}
                  />
                </DownloadProvider>
              </CatalogContext.Provider>
            </WorkspaceProvider>
          </UserProvider>
        </Wrapper>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
